<script lang="ts">
    /**
     * GridverseShowcaseSection - Gridverse 실제 게임 화면 쇼케이스
     *
     * GV의 핵심 화면 6종을 폰 목업 카드로 소개.
     * JadEye FeaturesSection 패턴(카드 그리드 + 풀 미리보기 모달) 차용 — 단,
     * 쇼케이스는 화면 자체가 주인공이라 썸네일 대신 카드에 폰 목업을 크게 노출.
     * 카드 클릭/탭 → 풀스크린 미리보기(모바일 hover 부재 대응). accent = gold(#B49B50).
     */
    let isVisible = $state(false);
    let previewImage = $state<string | null>(null);
    let previewAlt = $state('');

    $effect(() => {
        const t = setTimeout(() => {
            isVisible = true;
        }, 150);
        return () => clearTimeout(t);
    });

    function openPreview(img: string, name: string) {
        previewImage = img;
        previewAlt = `${name} 화면`;
    }

    function closePreview() {
        previewImage = null;
    }

    /** 같은 이름의 WebP 를 <source> 로 먼저 제시한다. PNG 는 폴백으로 남긴다. */
    const webp = (png: string) => png.replace(/\.png$/, '.webp');

    const shots = [
        {
            icon: '🗺️',
            img: '/images/gridverse/map.png',
            name: '걸어서 개척하는 영역',
            desc: '현실 공간을 걸으며 기지를 짓고, 헥사 영역을 넓혀가요.',
        },
        {
            icon: '🌌',
            img: '/images/gridverse/dimension.png',
            name: '나만의 차원 · AR',
            desc: '시즌마다 열리는 전용 차원을 3D와 AR로 자유롭게 탐험해요.',
        },
        {
            icon: '📜',
            img: '/images/gridverse/quest.png',
            name: '매일의 퀘스트',
            desc: '데일리·위클리 목표를 따라가며 꾸준히 성장해요.',
        },
        {
            icon: '🎓',
            img: '/images/gridverse/profile.png',
            name: '성장과 전문화',
            desc: '건설가·탐험가·사교가로 전문화하고, 다른 차원의 동료와 함께 개척해요.',
        },
        {
            icon: '📖',
            img: '/images/gridverse/codex.png',
            name: '수집과 도감',
            desc: '코스메틱과 랜드마크를 모으며 나만의 도감을 채워요.',
        },
        {
            icon: '⚡',
            img: '/images/gridverse/daily.png',
            name: '매일의 보상',
            desc: '출석·생산·걸음 변환으로 매일 에너지가 쌓여요.',
        },
    ];
</script>

<section id="gv-showcase" class="showcase-section">
    <div class="showcase-inner" class:visible={isVisible}>
        <div class="showcase-head">
            <h2 class="showcase-title">실제 게임 화면을 미리 만나요</h2>
            <p class="showcase-subtitle">Gridverse의 핵심 화면이에요. 탭하면 크게 볼 수 있어요.</p>
        </div>

        <div class="showcase-grid">
            {#each shots as s}
                <button class="shot-card" onclick={() => openPreview(s.img, s.name)} aria-label="{s.name} 화면 크게 보기">
                    <div class="shot-phone">
                        <picture>
                            <source srcset={webp(s.img)} type="image/webp" />
                            <img
                                src={s.img}
                                alt="{s.name} 화면"
                                class="shot-img"
                                width="1080"
                                height="2340"
                                loading="lazy"
                            />
                        </picture>
                    </div>
                    <div class="shot-caption">
                        <span class="shot-icon">{s.icon}</span>
                        <div class="shot-text">
                            <h3 class="shot-name">{s.name}</h3>
                            <p class="shot-desc">{s.desc}</p>
                        </div>
                    </div>
                </button>
            {/each}
        </div>
    </div>

    <!-- Full Preview Modal -->
    {#if previewImage}
        <div
            class="preview-overlay"
            role="button"
            tabindex="0"
            aria-label="미리보기 닫기"
            onclick={closePreview}
            onkeydown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') closePreview(); }}
        >
            <div class="preview-phone">
                <picture>
                    <source srcset={webp(previewImage)} type="image/webp" />
                    <img src={previewImage} alt={previewAlt} width="1080" height="2340" />
                </picture>
            </div>
        </div>
    {/if}
</section>

<style>
    .showcase-section {
        padding: 5rem 1.5rem;
        background: var(--landing-bg-alt, #f8f9fa);
    }

    .showcase-inner {
        max-width: 72rem;
        margin: 0 auto;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .showcase-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .showcase-head {
        text-align: center;
        margin-bottom: 3rem;
    }

    .showcase-title {
        font-size: clamp(1.8rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.75rem;
    }

    .showcase-subtitle {
        font-size: 1.05rem;
        color: var(--landing-text-sub, #666666);
        margin: 0;
    }

    .showcase-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1.5rem;
        max-width: 24rem;
        margin: 0 auto;
    }

    /* Card (button reset) */
    .shot-card {
        appearance: none;
        border: 1px solid rgba(0, 0, 0, 0.05);
        background: var(--landing-bg, #ffffff);
        border-radius: 20px;
        padding: 1.25rem 1.25rem 1.5rem;
        text-align: left;
        cursor: pointer;
        font: inherit;
        color: inherit;
        display: flex;
        flex-direction: column;
        gap: 1.1rem;
        transition:
            transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
            box-shadow 0.3s ease,
            border-color 0.3s ease;
    }

    .shot-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 14px 34px rgba(0, 0, 0, 0.09);
        border-color: rgba(180, 155, 80, 0.35);
    }

    .shot-card:focus-visible {
        outline: 2px solid #b49b50;
        outline-offset: 3px;
    }

    /* Phone mockup inside card */
    .shot-phone {
        width: 100%;
        aspect-ratio: 1080 / 2340;
        background: #1a1a1a;
        border-radius: 24px;
        padding: 8px;
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        overflow: hidden;
    }

    /* picture 가 박스를 만들지 않게 해 img 가 원래 자리(부모의 직접 자식)에 그대로 남는다. */
    picture {
        display: contents;
    }

    .shot-img {
        width: 100%;
        height: 100%;
        border-radius: 18px;
        object-fit: cover;
        object-position: top;
        display: block;
    }

    /* Caption */
    .shot-caption {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding: 0 0.25rem;
    }

    .shot-icon {
        font-size: 1.6rem;
        line-height: 1;
        flex-shrink: 0;
    }

    .shot-text {
        flex: 1;
    }

    .shot-name {
        font-size: 1.1rem;
        font-weight: 700;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.35rem;
    }

    .shot-desc {
        font-size: 0.9rem;
        line-height: 1.6;
        color: var(--landing-text-sub, #666666);
        margin: 0;
    }

    /* Preview Modal */
    .preview-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.65);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        cursor: zoom-out;
        padding: 2rem 1rem;
        animation: overlayIn 0.2s ease;
    }

    .preview-phone {
        width: 300px;
        max-width: 85vw;
        max-height: 86vh;
        background: #1a1a1a;
        border-radius: 32px;
        padding: 12px;
        box-shadow: 0 24px 70px rgba(0, 0, 0, 0.45);
    }

    .preview-phone img {
        width: 100%;
        height: auto;
        max-height: calc(86vh - 24px);
        object-fit: contain;
        border-radius: 22px;
        display: block;
    }

    @keyframes overlayIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @media (min-width: 640px) {
        .showcase-grid {
            grid-template-columns: 1fr 1fr;
            max-width: 44rem;
            gap: 1.75rem;
        }
    }

    @media (min-width: 768px) {
        .showcase-section {
            padding: 6rem 2.5rem;
        }
    }

    @media (min-width: 1024px) {
        .showcase-section {
            padding: 7rem 3rem;
        }

        .showcase-grid {
            grid-template-columns: repeat(3, 1fr);
            max-width: 66rem;
            gap: 2rem;
        }
    }
</style>
